import {
  traceWebAuthBootstrapRequestCompleted,
  traceWebAuthBootstrapRequestFailed,
  traceWebAuthBootstrapRequestRecovered,
  traceWebAuthBootstrapRequestStarted,
  traceWebAuthLoadClerkSessionCompleted,
  traceWebAuthTokenResolutionCompleted,
} from './bootstrapTraceEvents';
import {
  isMissingUserIdentityError,
  isUserNotFoundError,
  resolveTokenFromClerkClientSessions,
  wait,
} from './useAuth.helpers';
import type { AuthSession } from './types';

type LoadClerkSessionArgs = {
  client: unknown;
  getToken: () => Promise<string | null>;
  currentUserId: string | null;
  allowProvisioning: boolean;
  bootstrapSession: (
    accessToken: string,
    opts?: { allowProvisioning?: boolean },
  ) => Promise<AuthSession>;
  clearProvisioningFlag: () => void;
};

export const loadClerkSession = async ({
  client,
  getToken,
  currentUserId,
  allowProvisioning,
  bootstrapSession,
  clearProvisioningFlag,
}: LoadClerkSessionArgs): Promise<AuthSession | null> => {
  const loadStartedAt = Date.now();
  let token: string | null = null;
  let tokenAttemptCount = 0;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    tokenAttemptCount = attempt + 1;
    token = await getToken();
    token ??= await resolveTokenFromClerkClientSessions(client);
    if (token) break;
    await wait(100 * (attempt + 1));
  }
  traceWebAuthTokenResolutionCompleted({
    userId: currentUserId,
    durationMs: Date.now() - loadStartedAt,
    attemptCount: tokenAttemptCount,
    tokenResolved: token !== null,
  });
  if (!token) {
    traceWebAuthLoadClerkSessionCompleted({
      userId: currentUserId,
      durationMs: Date.now() - loadStartedAt,
      outcome: 'no_token',
    });
    return null;
  }

  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const bootstrapStartedAt = Date.now();
    traceWebAuthBootstrapRequestStarted({
      userId: currentUserId,
      attempt: attempt + 1,
      allowProvisioning,
    });
    try {
      const nextSession = await bootstrapSession(token, { allowProvisioning });
      traceWebAuthBootstrapRequestCompleted({
        userId: nextSession.userId,
        attempt: attempt + 1,
        allowProvisioning,
        durationMs: Date.now() - bootstrapStartedAt,
      });
      traceWebAuthLoadClerkSessionCompleted({
        userId: nextSession.userId,
        durationMs: Date.now() - loadStartedAt,
        outcome: 'success',
      });
      if (allowProvisioning) {
        clearProvisioningFlag();
      }
      return nextSession;
    } catch (caughtError) {
      if (isMissingUserIdentityError(caughtError)) {
        if (allowProvisioning) return null;
        const recovered = await bootstrapSession(token, {
          allowProvisioning: true,
        });
        traceWebAuthBootstrapRequestRecovered({
          userId: recovered.userId,
          attempt: attempt + 1,
          recoveryReason: 'missing_user_identity',
          durationMs: Date.now() - bootstrapStartedAt,
        });
        traceWebAuthLoadClerkSessionCompleted({
          userId: recovered.userId,
          durationMs: Date.now() - loadStartedAt,
          outcome: 'recovered',
        });
        clearProvisioningFlag();
        return recovered;
      }
      if (isUserNotFoundError(caughtError)) {
        if (allowProvisioning) return null;
        const recovered = await bootstrapSession(token, {
          allowProvisioning: true,
        });
        traceWebAuthBootstrapRequestRecovered({
          userId: recovered.userId,
          attempt: attempt + 1,
          recoveryReason: 'user_not_found',
          durationMs: Date.now() - bootstrapStartedAt,
        });
        traceWebAuthLoadClerkSessionCompleted({
          userId: recovered.userId,
          durationMs: Date.now() - loadStartedAt,
          outcome: 'recovered',
        });
        clearProvisioningFlag();
        return recovered;
      }
      traceWebAuthBootstrapRequestFailed({
        userId: currentUserId,
        attempt: attempt + 1,
        allowProvisioning,
        durationMs: Date.now() - bootstrapStartedAt,
        error: caughtError,
      });
      lastError = caughtError;
      if (attempt < 1) {
        await wait(150 * (attempt + 1));
      }
    }
  }
  traceWebAuthLoadClerkSessionCompleted({
    userId: currentUserId,
    durationMs: Date.now() - loadStartedAt,
    outcome: 'failed',
  });
  throw lastError;
};
